



































































































































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { RoomServiceAPI } from "@/api/room-service/room-service-api";
import {
  IRoomServiceCommunication,
  IRoomServiceRequest,
  TRoomServicePriority,
  TRoomServiceStatus,
} from "@/@types/room-service/room-service";
import { AccessToken } from "@/services/accessDecoder";

@Component({})
class RequestDetailModal extends Vue {
  @Prop({ type: Boolean, default: false }) show!: boolean;
  @Prop({ type: Object as () => IRoomServiceRequest | null, default: null })
  request!: IRoomServiceRequest | null;

  loading = false;
  note = "";
  assignName = "";
  priorityLocal: TRoomServicePriority = "MEDIUM";
  priorities: TRoomServicePriority[] = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];

  @Watch("request")
  onRequest(req: IRoomServiceRequest | null): void {
    if (req) {
      this.priorityLocal = req.priority;
      this.assignName = req.assignedTo?.staffName || "";
      this.note = "";
    }
  }

  get statusKey(): string {
    return (this.request?.status || "created").toLowerCase();
  }
  get priorityClass(): string {
    return (this.request?.priority || "medium").toLowerCase();
  }
  get isEmergency(): boolean {
    const r = this.request;
    if (!r) return false;
    return (
      r.serviceType === "EMERGENCY_ASSISTANCE" ||
      r.priority === "CRITICAL" ||
      r.status === "ESCALATED"
    );
  }

  get canAccept(): boolean {
    return this.request?.status === "CREATED";
  }
  get canStart(): boolean {
    return (
      this.request?.status === "CREATED" || this.request?.status === "ASSIGNED"
    );
  }
  get canComplete(): boolean {
    return (
      this.request?.status === "IN_PROGRESS" ||
      this.request?.status === "ASSIGNED" ||
      this.request?.status === "ESCALATED"
    );
  }
  get canCancel(): boolean {
    return (
      this.request?.status === "CREATED" ||
      this.request?.status === "ASSIGNED" ||
      this.request?.status === "IN_PROGRESS" ||
      this.request?.status === "ESCALATED"
    );
  }

  get detailsText(): string {
    const d = this.request?.details;
    if (!d) return "";
    return d.description || d.specialInstructions || "";
  }

  get timeline(): Array<{ kind: string; title: string; meta: string }> {
    const r = this.request;
    if (!r) return [];
    const items: { kind: string; title: string; meta: string }[] = [];
    const fmt = (v?: Date | string) => {
      if (!v) return "";
      return new Date(v).toLocaleString(
        this.$i18n.locale === "ru" ? "ru-RU" : "en-US"
      );
    };

    if (r.timestamps?.createdAt || r.createdAt)
      items.push({
        kind: "created",
        title: String(this.$t("roomService.timeline.created")),
        meta: fmt(r.timestamps?.createdAt || r.createdAt),
      });
    if (r.timestamps?.acceptedAt || r.assignedTo?.assignedAt)
      items.push({
        kind: "assigned",
        title:
          String(this.$t("roomService.timeline.assigned")) +
          (r.assignedTo?.staffName ? ": " + r.assignedTo.staffName : ""),
        meta: fmt(r.timestamps?.acceptedAt || r.assignedTo?.assignedAt),
      });
    if (r.timestamps?.startedAt)
      items.push({
        kind: "progress",
        title: String(this.$t("roomService.timeline.started")),
        meta: fmt(r.timestamps.startedAt),
      });
    if (r.timestamps?.completedAt)
      items.push({
        kind: "completed",
        title: String(this.$t("roomService.timeline.completed")),
        meta: fmt(r.timestamps.completedAt),
      });
    if (r.timestamps?.cancelledAt)
      items.push({
        kind: "cancelled",
        title: String(this.$t("roomService.timeline.cancelled")),
        meta: fmt(r.timestamps.cancelledAt),
      });

    const comms = (r.communications || []) as IRoomServiceCommunication[];
    comms
      .filter((c) => c.type === "NOTE")
      .forEach((c) =>
        items.push({
          kind: "note",
          title: (c.author || "") + ": " + (c.content || ""),
          meta: fmt(c.timestamp),
        })
      );

    return items;
  }

  close(): void {
    this.$emit("close");
  }

  async setStatus(status: TRoomServiceStatus): Promise<void> {
    if (!this.request || this.loading) return;
    this.loading = true;
    try {
      const res = await RoomServiceAPI.updateForAdmin(this.request._id, {
        status,
      });
      if (res.status === 200) {
        this.$notify({
          group: "global",
          text: res.data.message || String(this.$i18n.t("alerts.save")),
          type: "success",
        });
        this.$emit("updated", res.data.request);
      }
    } catch (e) {
      this.onError();
    } finally {
      this.loading = false;
    }
  }

  async updatePriority(): Promise<void> {
    if (!this.request || this.loading) return;
    if (this.priorityLocal === this.request.priority) return;
    this.loading = true;
    try {
      const res = await RoomServiceAPI.updateForAdmin(this.request._id, {
        priority: this.priorityLocal,
      });
      if (res.status === 200) {
        this.$notify({
          group: "global",
          text: String(this.$i18n.t("alerts.save")),
          type: "success",
        });
        this.$emit("updated", res.data.request);
      }
    } catch (e) {
      this.onError();
    } finally {
      this.loading = false;
    }
  }

  async assign(): Promise<void> {
    if (!this.request || !this.assignName || this.loading) return;
    this.loading = true;
    try {
      const res = await RoomServiceAPI.updateForAdmin(this.request._id, {
        assignedTo: {
          staffId: AccessToken.id || this.assignName,
          staffName: this.assignName,
          staffRole: AccessToken.payload?.rules || "staff",
          assignedAt: new Date().toISOString(),
        },
      });
      if (res.status === 200) {
        this.$notify({
          group: "global",
          text: String(this.$i18n.t("alerts.save")),
          type: "success",
        });
        this.$emit("updated", res.data.request);
      }
    } catch (e) {
      this.onError();
    } finally {
      this.loading = false;
    }
  }

  async addNote(): Promise<void> {
    if (!this.request || !this.note.trim() || this.loading) return;
    this.loading = true;
    try {
      const res = await RoomServiceAPI.updateForAdmin(this.request._id, {
        note: this.note.trim(),
      });
      if (res.status === 200) {
        this.$notify({
          group: "global",
          text: String(this.$i18n.t("alerts.save")),
          type: "success",
        });
        this.note = "";
        this.$emit("updated", res.data.request);
      }
    } catch (e) {
      this.onError();
    } finally {
      this.loading = false;
    }
  }

  onError(): void {
    this.$notify({
      group: "global",
      text: String(this.$i18n.t("alerts.emptyUpdate")),
      type: "warn",
    });
  }
}
export default RequestDetailModal;
